<script setup lang="ts">
defineOptions({ name: "AppSidebar" });
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@shared/store/modules/app";
import { usePermissionStore } from "@shared/store/modules/permission";
import { prefetchRouteComponent } from "@shared/router/prefetchRouteComponent";
import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import { collectNavigableLeafPaths, collectVisiblePages, findFirstNavigableLeafPath } from "@shared/utils/sidebarMenu";
import SubMenuPanel from "./SubMenuPanel.vue";
import logoUrl from "@shared/assets/logo.svg";
import { getAppHomePath } from "@shared/utils/appHome";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const permissionStore = usePermissionStore();

function getMenuIconClass(item: SidebarMenuItem, active: boolean): string {
    if (item.icon && item.selectIcon) {
        return active ? item.selectIcon : item.icon;
    }
    return active ? "icon-product-active" : "icon-product-default";
}

const visibleMenus = computed<SidebarMenuItem[]>(() => permissionStore.sidebarMenus);

const activeIndex = computed(() => {
    const currentPath = (route.meta?.activeMenu as string | undefined) || route.path;
    let matchedIndex = -1;
    let matchedLength = -1;

    visibleMenus.value.forEach((item, index) => {
        for (const leafPath of collectNavigableLeafPaths(item)) {
            if (currentPath !== leafPath && !currentPath.startsWith(`${leafPath}/`)) continue;
            if (leafPath.length > matchedLength) {
                matchedLength = leafPath.length;
                matchedIndex = index;
            }
        }
    });

    return matchedIndex;
});

function isActive(index: number) {
    return activeIndex.value === index;
}

function hasSubmenu(item: SidebarMenuItem): boolean {
    return collectVisiblePages(item).length > 1 || item.type === "group";
}

function resolveMenuTarget(item: SidebarMenuItem): string | undefined {
    return findFirstNavigableLeafPath(item);
}

function handleMouseEnter(item: SidebarMenuItem) {
    if (!hasSubmenu(item)) {
        appStore.closeSubMenu();
        return;
    }
    appStore.openSubMenu(item);
    const target = resolveMenuTarget(item);
    if (target) prefetchRouteComponent(router, target);
}

function handleClickItem(item: SidebarMenuItem) {
    // 分组：只打开下级面板，禁止跳转
    if (item.type === "group") {
        if (hasSubmenu(item)) {
            appStore.openSubMenu(item);
        }
        return;
    }
    const target = resolveMenuTarget(item);
    if (!target) return;
    router.push(target);
    appStore.closeSubMenu();
}

function handleLeaveSidebar() {
    // 离开侧边栏区域时不立即关闭，由 SubMenuPanel 自身或 mask 遮罩处理
}
</script>

<template>
    <aside class="sidebar" @mouseleave="handleLeaveSidebar">
        <div class="sidebar-logo" @click="router.push(getAppHomePath())">
            <img :src="logoUrl" alt="logo" class="logo-img" />
        </div>

        <div class="menu-list">
            <div
                v-for="(item, index) in visibleMenus"
                :key="item.id"
                class="menu-item"
                :class="{ 'is-active': isActive(index) }"
                :data-testid="`sidebar-menu-${item.title || item.path || index}`"
                :data-menu-title="item.title"
                @mouseenter="handleMouseEnter(item)"
                @click="handleClickItem(item)"
            >
                <i class="iconfont menu-icon" :class="getMenuIconClass(item, isActive(index))" />
                <span class="menu-title">{{ item.title }}</span>
            </div>
        </div>

        <SubMenuPanel />
    </aside>
</template>

<style lang="scss" scoped>
.sidebar {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: var(--main-background);
}

.sidebar-logo {
    flex-shrink: 0;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .logo-img {
        width: var(--sidebar-logo-size);
        height: var(--sidebar-logo-size);
        object-fit: contain;
    }
}

.menu-list {
    flex: 1;
    overflow-y: auto;
    padding: 0;

    @include scrollbar(4px);
}

.menu-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: var(--sidebar-menu-text-gap);
    height: var(--sidebar-menu-item-height);
    padding: 0 8px 0 12px;
    cursor: pointer;
    user-select: none;
    color: var(--main-color-hover);
    background-color: transparent;
    transition:
        color var(--transition-base),
        background-color var(--transition-base);
    position: relative;
    font-size: var(--font-size-label);
    line-height: 22px;

    .menu-icon {
        flex-shrink: 0;
        font-size: var(--sidebar-menu-icon-size);
        line-height: 1;
        color: inherit;
    }

    .menu-title {
        flex: 0 1 auto;
        min-width: 0;
        font-size: var(--font-size-label);
        line-height: 22px;
        white-space: nowrap;
        @include ellipsis;
    }

    &:hover {
        color: var(--primary);
        background-color: var(--main-background);
        font-weight: var(--font-weight-medium);
    }

    &.is-active {
        color: var(--primary);
        background-color: var(--main-background);
        font-weight: var(--font-weight-semibold);

        &::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            height: var(--sidebar-active-indicator-height);
            width: var(--sidebar-active-indicator-width);
            background-color: var(--primary);
            border-radius: 0;
        }
    }
}
</style>
